import { useEffect, useRef, useState } from 'react';
import { Button } from '@lms/ui';
import { X, BookOpen } from 'lucide-react';
import { useCreateSubject, useUpdateSubject } from '../hooks/useProgramSubjects';
import type { ProgramLevel, ProgramSubject } from '@lms/shared/types';
import { useLanguage } from '../lib/LanguageContext';
import { copy } from '../copy/copy';
import { Bilingual } from './Bilingual';
import { SubjectPrerequisitePicker } from './SubjectPrerequisitePicker';
import { SubjectPrerequisiteVerificationError } from '../lib/subject-prerequisite-save';

interface SubjectModalProps {
  programId: string;
  stageId: string;
  stageName?: string;
  order: number;
  subject?: ProgramSubject;
  availablePrerequisites?: ProgramSubject[];
  prerequisiteLevels?: ProgramLevel[];
  onClose: () => void;
}

export function SubjectModal({
  programId,
  stageId,
  stageName,
  order,
  subject,
  availablePrerequisites = [],
  prerequisiteLevels,
  onClose,
}: SubjectModalProps) {
  const { t } = useLanguage();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => dialog?.close();
  }, []);
  const [name, setName] = useState(subject?.name || '');
  const [code, setCode] = useState(subject?.code || '');
  const [kitabRujukan, setKitabRujukan] = useState(subject?.kitabRujukan || '');
  const [teacherName, setTeacherName] = useState(subject?.teacherName || '');
  const [isSequential, setIsSequential] = useState(subject?.isSequential ?? true);
  const [requireEvaluationPassForNextMaterial, setRequireEvaluationPassForNextMaterial] = useState(
    subject?.requireEvaluationPassForNextMaterial ?? false
  );
  const [prerequisiteSubjectId, setPrerequisiteSubjectId] = useState(
    subject?.prerequisiteSubjectId || ''
  );
  const [hasPrerequisite, setHasPrerequisite] = useState(Boolean(subject?.prerequisiteSubjectId));
  const [prerequisiteError, setPrerequisiteError] = useState(false);
  const [description, setDescription] = useState(subject?.description || '');

  const createSubjectMutation = useCreateSubject();
  const updateSubjectMutation = useUpdateSubject();

  const isEditing = !!subject;
  const isPending = createSubjectMutation.isPending || updateSubjectMutation.isPending;

  // Filter out current subject from available prerequisites
  const filteredPrereqs = availablePrerequisites.filter(p => !isEditing || p.id !== subject.id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isPending) return;
    if (hasPrerequisite && !prerequisiteSubjectId) {
      setPrerequisiteError(true);
      return;
    }

    if (isEditing) {
      updateSubjectMutation.mutate(
        {
          id: subject.id,
          programId,
          data: {
            name: name.trim(),
            code: code.trim() || undefined,
            kitabRujukan: kitabRujukan.trim() || undefined,
            teacherName: teacherName.trim() || undefined,
            isSequential,
            requireEvaluationPassForNextMaterial,
            prerequisiteSubjectId: hasPrerequisite ? prerequisiteSubjectId : null,
            description: description.trim() || undefined,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    } else {
      createSubjectMutation.mutate(
        {
          programId,
          data: {
            stageId,
            name: name.trim(),
            code: code.trim() || undefined,
            kitabRujukan: kitabRujukan.trim() || undefined,
            teacherName: teacherName.trim() || undefined,
            isSequential,
            requireEvaluationPassForNextMaterial,
            prerequisiteSubjectId: hasPrerequisite ? prerequisiteSubjectId : undefined,
            description: description.trim() || undefined,
            order,
          },
        },
        {
          onSuccess: () => onClose(),
        }
      );
    }
  };

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="subject-editor-title"
      aria-busy={isPending}
      onCancel={event => {
        event.preventDefault();
        if (!isPending) onClose();
      }}
      className="fixed inset-0 m-auto max-h-[90dvh] w-[calc(100%-2rem)] max-w-2xl overflow-y-auto rounded-2xl bg-surface p-0 text-on-surface shadow-level5 backdrop:bg-on-surface/30 backdrop:backdrop-blur-xl"
    >
      <div>
        <div className="p-4 sm:p-6 flex justify-between items-center gap-3 bg-surface-container-low">
          <div className="flex min-w-0 items-center gap-3">
            <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <BookOpen size={20} aria-hidden="true" />
            </div>
            <div className="min-w-0 break-words">
              <h3
                id="subject-editor-title"
                className="text-lg font-headline font-bold text-on-surface m-0"
              >
                <Bilingual
                  {...(isEditing ? copy.subjectModal.titleEdit : copy.subjectModal.titleCreate)}
                />
              </h3>
              <p className="text-sm text-on-surface-variant break-words">
                {isEditing
                  ? subject.name
                  : stageName
                    ? `${stageName} · ${t(copy.subjectModal.subjectNumber).replace('{number}', String(order))}`
                    : t(copy.subjectModal.subjectNumber).replace('{number}', String(order))}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            aria-label={t(copy.subjectModal.closeEditor)}
            className="flex h-11 w-11 shrink-0 items-center justify-center hover:bg-surface-container-high active:bg-surface-container-highest rounded-lg text-on-surface-variant focus-visible:outline-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
          {(createSubjectMutation.isError || updateSubjectMutation.isError) && (
            <p
              role="alert"
              className="rounded-xl bg-error-container p-3 text-sm text-on-error-container"
            >
              {t(
                updateSubjectMutation.error instanceof SubjectPrerequisiteVerificationError
                  ? updateSubjectMutation.error.reason === 'mismatch'
                    ? copy.subjectModal.prerequisiteSaveMismatch
                    : copy.subjectModal.prerequisiteSaveUnverified
                  : copy.subjectModal.saveFailed
              )}
            </p>
          )}
          <fieldset disabled={isPending} className="min-w-0 space-y-5 disabled:opacity-60">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-2">
                <label
                  htmlFor="subject-name"
                  className="block text-sm font-semibold text-on-surface"
                >
                  <Bilingual {...copy.subjectModal.labelName} />{' '}
                  <span className="text-error">*</span>
                </label>
                <input
                  id="subject-name"
                  type="text"
                  required
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderName)}
                  className="min-h-12 w-full px-4 py-3 bg-surface-container-high rounded-xl text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject-code"
                  className="block text-sm font-semibold text-on-surface"
                >
                  <Bilingual {...copy.subjectModal.labelCode} />
                </label>
                <input
                  id="subject-code"
                  type="text"
                  value={code}
                  onChange={e => setCode(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderCode)}
                  className="min-h-12 w-full px-4 py-3 bg-surface-container-high rounded-xl text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="subject-kitab"
                  className="block text-sm font-semibold text-on-surface"
                >
                  <Bilingual {...copy.subjectModal.labelKitab} />
                </label>
                <input
                  id="subject-kitab"
                  type="text"
                  value={kitabRujukan}
                  onChange={e => setKitabRujukan(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderKitab)}
                  className="min-h-12 w-full px-4 py-3 bg-surface-container-high rounded-xl text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="subject-teacher"
                  className="block text-sm font-semibold text-on-surface"
                >
                  <Bilingual {...copy.subjectModal.labelTeacher} />
                </label>
                <input
                  id="subject-teacher"
                  type="text"
                  value={teacherName}
                  onChange={e => setTeacherName(e.target.value)}
                  placeholder={t(copy.subjectModal.placeholderTeacher)}
                  className="min-h-12 w-full px-4 py-3 bg-surface-container-high rounded-xl text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                />
              </div>
            </div>

            <SubjectPrerequisitePicker
              enabled={hasPrerequisite}
              value={prerequisiteSubjectId}
              subjects={filteredPrereqs}
              levels={prerequisiteLevels}
              disabled={isPending}
              error={prerequisiteError}
              onEnabledChange={enabled => {
                setHasPrerequisite(enabled);
                setPrerequisiteError(false);
                if (!enabled) setPrerequisiteSubjectId('');
              }}
              onChange={value => {
                setPrerequisiteSubjectId(value);
                setPrerequisiteError(false);
              }}
            />

            <div className="flex items-center gap-3 p-3 bg-surface-container-low rounded-xl">
              <input
                type="checkbox"
                id="subject-sequential"
                checked={isSequential}
                onChange={e => setIsSequential(e.target.checked)}
                className="rounded border-outline-variant text-primary focus:ring-primary h-4 w-4"
              />
              <label
                htmlFor="subject-sequential"
                className="text-sm font-semibold text-on-surface cursor-pointer select-none"
              >
                <span className="block font-bold">
                  <Bilingual {...copy.subjectModal.labelSequential} />
                </span>
                <span className="mt-1 block text-sm leading-relaxed text-on-surface-variant font-normal">
                  <Bilingual {...copy.subjectModal.descSequential} />
                </span>
              </label>
            </div>

            <div className="flex items-start gap-3 rounded-xl bg-secondary-container/45 p-4">
              <input
                type="checkbox"
                id="subject-evaluation-gate"
                checked={requireEvaluationPassForNextMaterial}
                onChange={e => setRequireEvaluationPassForNextMaterial(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded text-primary focus:ring-primary"
              />
              <label
                htmlFor="subject-evaluation-gate"
                className="cursor-pointer select-none text-sm font-semibold text-on-surface"
              >
                <span className="block font-bold">
                  <Bilingual {...copy.subjectModal.labelEvaluationGate} />
                </span>
                <span className="mt-1 block text-sm font-normal leading-relaxed text-on-surface-variant">
                  <Bilingual {...copy.subjectModal.descEvaluationGate} />
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <label htmlFor="subject-desc" className="block text-sm font-semibold text-on-surface">
                <Bilingual {...copy.subjectModal.labelDescription} />
              </label>
              <textarea
                id="subject-desc"
                rows={2}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder={t(copy.subjectModal.placeholderDescription)}
                className="w-full px-4 py-3 bg-surface-container-high rounded-xl text-base text-on-surface placeholder:text-on-surface-variant focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary resize-y"
              />
            </div>
          </fieldset>

          <div className="flex flex-wrap justify-end gap-3 pt-4">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isPending}>
              <Bilingual {...copy.levelModal.abort} />
            </Button>
            <Button variant="primary" type="submit" disabled={isPending || !name.trim()}>
              {isPending ? (
                <Bilingual {...copy.subjectModal.btnSaving} />
              ) : isEditing ? (
                <Bilingual {...copy.subjectModal.btnSubmitEdit} />
              ) : (
                <Bilingual {...copy.subjectModal.btnSubmitCreate} />
              )}
            </Button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
